import {CaretDownFilled, CaretUpFilled} from '@ant-design/icons';
import {Button, Table, Typography} from 'antd';
import React, {useState} from 'react';
import styles from './styles';
import CommentsDialog from 'Pages/SRDashboard/components/ResultsTable/CommentsDialog';
import {useCSContext} from 'Context/ContentStackContext';
import {SortOrder} from 'Utils/enums';

/**
 * IMPORTANT: header cells (from `headers`) and row cells (from `Object.keys(row)`)
 * are two INDEPENDENT lists, paired only by position after each is filtered by
 * hideColumns — NOT matched by header.value === row key name. Several real call
 * sites (Benefits, Events, the cashback ticket list) rely on this: their header
 * `value`s don't actually match their row's key names, and only render correctly
 * today because the arrays line up positionally. This rewrite preserves that
 * exact positional pairing (each column's `render` recomputes the row's own
 * filtered key list and reads the value at that column's index) rather than
 * switching to antd's normal name-keyed `dataIndex`, so none of those pages break.
 */
const TableView = (props) => {
  const {
    headers,
    isCommentColumn,
    hideColumns,
    sortingEnabled,
    sortOrder,
    currentSortCriteria,
    onRowClick = () => {},
    isRowDisabled = () => false,
  } = props;
  const {artifacts} = useCSContext();
  const [openCommentsDialog, setOpenCommentsDialog] = useState(false);
  const [commentData, setComment] = useState('');

  const hideCommentModal = (isOpen) => {
    setOpenCommentsDialog(isOpen);
  };

  const showCommentModal = (e, data) => {
    e.stopPropagation();
    setComment(data?.srData?.additionalData?.comments);
    setOpenCommentsDialog(true);
  };

  const allHeaders = isCommentColumn
    ? [...headers, {label: 'Comments', value: 'moreInfo'}]
    : headers;
  const visibleHeaders = (allHeaders || []).filter(
    (item: any) => !(hideColumns && hideColumns[item.value]),
  );

  const renderCell = (key: string, data: any) => {
    if (key === 'moreInfo') {
      return (
        <Button
          type="text"
          size="small"
          onClick={(e) => showCommentModal(e, data)}
          icon={<img src={artifacts.more_icon} alt="comment" />}
        />
      );
    }
    if (key === 'buttons' && props?.buttons && props?.buttons?.length > 0) {
      return props.buttons.map((buttonData) => (
        <Button
          key={buttonData.id || buttonData.name}
          onClick={(e) => {
            e.stopPropagation();
            props.customEvents[buttonData.name](data);
          }}
          type="primary"
          size="small"
          disabled={buttonData.isDisabled}
          id={buttonData.id}
          style={{marginRight: 4, marginBottom: 4}}>
          {buttonData.fieldLabel}
        </Button>
      ));
    }
    return <Typography.Text>{data[key] || ''}</Typography.Text>;
  };

  const columns = visibleHeaders.map((header: any, colIndex: number) => ({
    key: `${header.value}_${colIndex}`,
    title: (
      <div
        onClick={
          sortingEnabled
            ? () => (props.sortTable && props.sortTable(header.value)) || {}
            : undefined
        }
        style={{
          display: 'flex',
          alignItems: 'center',
          cursor: sortingEnabled ? 'pointer' : undefined,
        }}>
        {header.label}
        {sortingEnabled && currentSortCriteria === header.value ? (
          sortOrder === SortOrder.ASCENDING ? (
            <CaretUpFilled style={{fontSize: 12, marginLeft: 4}} />
          ) : sortOrder === SortOrder.DESCENDING ? (
            <CaretDownFilled style={{fontSize: 12, marginLeft: 4}} />
          ) : null
        ) : null}
      </div>
    ),
    render: (_: any, record: any) => {
      const visibleKeys = Object.keys(record).filter((k) => !(hideColumns && hideColumns[k]));
      const key = visibleKeys[colIndex];
      return key === undefined ? null : renderCell(key, record);
    },
  }));

  const dataSource = (props.tableData || []).map((item: any) =>
    isCommentColumn ? {...item, moreInfo: ''} : {...item},
  );

  return (
    <>
      <CommentsDialog
        openCommentsDialog={openCommentsDialog}
        setOpenCommentsDialog={hideCommentModal}
        comments={commentData}
      />
      <Table
        style={styles.paper}
        columns={columns}
        dataSource={dataSource}
        rowKey={(_, index) => index ?? 0}
        pagination={false}
        onRow={(record) => {
          const disabled = isRowDisabled(record);
          return {
            onClick: () => !disabled && onRowClick(record),
            style: {
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.5 : 1,
            },
          };
        }}
      />
    </>
  );
};

export default TableView;
